import styled from '@emotion/styled';
import { ButtonVariants, Icon, IconNames, rem } from '@triumph/design-system';
import { InputText } from 'primereact/inputtext';
import { useState } from 'react';

import { FileDragProps } from '@/components/hooks/useOpenS3File';
import { PERMISSION_DENIED_TITLE } from '@/components/shared/constants/features';

import { FileButton } from '../../Applicant.StyledComponents';
import { Document } from '../../Applicant.types';

interface EditableFilenameCellProps {
    file: Document;
    onFileClick: (fileId: string) => void;
    dragProps?: FileDragProps;
    onSaveFilename: (fileId: string, newFilename: string) => void;
    hasViewPermission: boolean;
    hasEditPermission: boolean;
}

const FilenameCell = styled.div`
    display: flex;
    align-items: center;
    gap: ${rem(8)};
    position: relative;
    width: 100%;
    min-width: 0;
`;

const EditableInput = styled(InputText)`
    flex: 1;
    font-size: ${rem(14)};
    border-left: none;
    border-right: none;
    border-top: none;
    border-bottom: 2px solid #2679ad;
    border-radius: 0;
    padding-left: 0;
    padding-right: 0;
    box-shadow: none;

    &:enabled:focus {
        outline: none;
        border-bottom-color: #2679ad;
        box-shadow: none;
    }

    &:enabled:hover {
        border-bottom-color: #2679ad;
    }
`;

const ActionIconsWrapper = styled.div`
    display: flex;
    gap: ${rem(4)};
    align-items: center;
`;

const IconButton = styled.button`
    border: none;
    background: none;
    padding: ${rem(4)};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: opacity 0.2s;

    &:hover {
        opacity: 0.8;
    }

    &:disabled {
        cursor: not-allowed;
        opacity: 0.5;
    }

    &.edit-icon-button {
        svg {
            color: #0ea5e9;
        }

        &:hover svg {
            color: #0284c7;
        }
    }
`;

const EditIcon = styled(Icon)`
    color: inherit;
`;

const SaveIcon = styled(Icon)`
    color: #22c55e;
`;

const CancelIcon = styled(Icon)`
    color: #ef4444;
`;

const ApplicantDocumentFileButton = styled(FileButton)`
    min-width: 0;
    max-width: 100%;

    > div {
        font-size: ${rem(14)};
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        display: block;
        max-width: 100%;
    }

    &:disabled > div {
        text-decoration: none;
    }

    &:not(:disabled) > div {
        text-decoration: underline;
        text-underline-offset: ${rem(4)};
    }
`;

export const EditableFilenameCell = ({
    file,
    onFileClick,
    dragProps,
    onSaveFilename,
    hasViewPermission,
    hasEditPermission,
}: EditableFilenameCellProps) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedFilename, setEditedFilename] = useState('');
    const [fileExtension, setFileExtension] = useState('');

    // Helper to split filename and extension
    const splitFilename = (filename: string) => {
        const lastDotIndex = filename.lastIndexOf('.');
        if (lastDotIndex === -1 || lastDotIndex === 0) {
            return { name: filename, extension: '' };
        }
        return {
            name: filename.substring(0, lastDotIndex),
            extension: filename.substring(lastDotIndex),
        };
    };

    const handleEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        const { name, extension } = splitFilename(file.fileName);
        setIsEditing(true);
        setEditedFilename(name);
        setFileExtension(extension);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedFilename('');
        setFileExtension('');
    };

    const handleSaveFilename = () => {
        if (!editedFilename.trim()) {
            return;
        }

        // Add the extension back when saving
        const fullFilename = editedFilename.trim() + fileExtension;
        onSaveFilename(file.fileId, fullFilename);
        setIsEditing(false);
        setEditedFilename('');
        setFileExtension('');
    };

    const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            handleSaveFilename();
        } else if (e.key === 'Escape') {
            handleCancelEdit();
        }
    };

    if (isEditing) {
        return (
            <FilenameCell>
                <EditableInput
                    id={`edit-filename-${file.fileId}`}
                    value={editedFilename}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setEditedFilename(e.target.value)
                    }
                    onKeyDown={handleKeyPress}
                    autoFocus
                    disabled={false}
                />
                <ActionIconsWrapper>
                    <IconButton
                        onClick={handleSaveFilename}
                        disabled={false}
                        title="Save"
                        type="button"
                        data-testid="save-filename-btn"
                    >
                        <SaveIcon
                            name={IconNames.Interface_Checkmark}
                            id={`save-filename-${file.fileId}`}
                            size={18}
                            altText="Save"
                        />
                    </IconButton>
                    <IconButton
                        onClick={handleCancelEdit}
                        disabled={false}
                        title="Cancel"
                        type="button"
                        data-testid="cancel-filename-btn"
                    >
                        <CancelIcon
                            name={IconNames.Danger_CloseX}
                            id={`cancel-edit-${file.fileId}`}
                            size={18}
                            altText="Cancel"
                        />
                    </IconButton>
                </ActionIconsWrapper>
            </FilenameCell>
        );
    }

    return (
        <FilenameCell>
            <ApplicantDocumentFileButton
                variant={ButtonVariants.tertiary}
                onClick={() => onFileClick(file.fileId)}
                {...dragProps}
                data-testid="filename-btn"
                disabled={!hasViewPermission}
                title={!hasViewPermission ? PERMISSION_DENIED_TITLE : file.fileName}
            >
                {file.fileName}
            </ApplicantDocumentFileButton>
            {hasEditPermission && (
                <IconButton
                    onClick={handleEditClick}
                    title="Edit filename"
                    type="button"
                    className="edit-icon-button"
                    data-testid="edit-filename-icon"
                >
                    <EditIcon
                        name={IconNames.Interface_Pencil}
                        id={`edit-icon-${file.fileId}`}
                        size={16}
                        altText="Edit filename"
                    />
                </IconButton>
            )}
        </FilenameCell>
    );
};
